import { BaseUser, MeetingShareholderInvitee } from '@orgbrain/lib-admin';
import { EventsImportWarning } from '../imports/events/events-import-pipeline';
import { ShareNumbers } from '../numbers';
import { ISONationalCode2Letter, ISONationalCode3Letter } from './beneficial-owners-altinn-types';
import { EmissionPlan } from './emission-plan';
import { MongoId } from '@orgbrain/lib-data';

export class State {
  // shareholders: { [id: string]: any } = {};
  records: Array<ShareholderRecord> = [];
  organizationId?: string;
  _id?: string;
  accounttype?: string;
  highestIssuedShareNumber?: number;

  static isVirgin(state: State): boolean {
    return state.records.length === 0;
  }
  static clone(a: State): State {
    const clone = new State();

    a.records.forEach((record) => {
      const recordClone = ShareholderRecord.clone(record);
      clone.records.push(recordClone);
    });

    return clone;
  }

  static findShareRecord(state: State, _id: string, shareclassId: string): ShareholderRecord {
    state.records = (state && state.records) || [];
    const ret = state.records.filter(
      (rec) => rec.entityId === _id && rec.shareclassId === shareclassId
    );
    if (ret.length === 0) {
      throw new Error(`Did not find shareRecord ${_id}   ${shareclassId}`);
    } else if (ret.length > 1) {
      throw new Error(`Found  ${ret.length} shareRecords  ${_id}   ${shareclassId}`);
    }
    return ret[0];
  }
}

export class ShareClass {
  /// WARNIN G USED IN FORMGROUP BUILDER DO NOT ADD ARRAYS
  _id?: MongoId; // unique key   but not return is schema is embedded in anlother schema so make optional

  organizationId: MongoId;
  name: string = null; // users given name
  comment?: string;
  par_value? = 1;
  voteWeight? = 1;
  // Probably best to assume rest is transient
  amount?: number; // ????
  number_from?;
  number_to?;
  createMe?: boolean;
  current_par_value?: number;
  maxVotesToUse?: number;
  createdAt?: Date;

  constructor({
    _id,
    name,
    organizationId,
    createMe,
  }: {
    _id: string;
    name: string;
    organizationId?: string;
    createMe?: boolean;
  }) {
    this._id = _id;
    this.name = name;
    if (organizationId) this.organizationId = organizationId;
    if (createMe) this.createMe = createMe;
  }
}

export class EnityClaim {
  /// WARNIN G USED IN FORMGROUP BUILDER DO NOT ADD ARRAYS
  _id: string; // unique key

  date: string = '';
  amount: string = '';
  referenceNumber: string = '';
  claimOwner: string = '';
  orgNumber: string = '';
  address: string = '';
  notes: string = '';
  createdAt: string;
  updatedAt: string;
  state: string = '';
}

export enum ShareholderEventType {
  edit = 'edit',
  import = 'import',
  emission = 'emission',
  transaction = 'transaction',
  establish = 'establish',
  split = 'split',
  splice = 'splice',
  sharedelete = 'sharedelete',
  dividend = 'dividend',
  parvaluechange = 'parvaluechange',
  moveShares = 'moveShares',
  refreshShareNumbers = 'refreshShareNumbers',
  capitalValue = 'capitalValue',
}

export class ShareholderEvent {
  _id?: string;
  organizationId: string;
  type: ShareholderEventType = null;
  creator: string = null;
  date: Date;
  createdAt?: Date;
  price?: number;
  deleted?: boolean;
  replacedEventId?: string;
  irrelevantForCompanyValue? = false;

  constructor({
    type,
    organizationId,
    creator,
    date,
    price,
    replacedEventId,
    irrelevantForCompanyValue,
  }: {
    type: ShareholderEventType;
    organizationId: string;
    creator: string;
    date: Date;
    price?: number;
    replacedEventId?: string;
    irrelevantForCompanyValue?: boolean;
  }) {
    this.type = type;
    this.organizationId = organizationId;
    this.creator = creator;
    if (irrelevantForCompanyValue) {
      this.irrelevantForCompanyValue = irrelevantForCompanyValue;
    }
    if (date) {
      this.date = new Date(date);
    } else {
      this.date = new Date(0);
    }
    if (price) {
      this.price = price;
    }
    if (replacedEventId) {
      this.replacedEventId = replacedEventId;
    }
  }
}
export interface DividendInformationEmailPayload {
  shareclass: ShareClass;
  date: Date;
  paymentdate: Date;
  totalshares: number;
  emailText: string;
  selected: Entity[];
  dividend: number;
  state: State;
  records?: ShareholderRecord[];
  subType?: EventDataDividendSubtype;
}
export class EventsOnboardPayload {
  events: ShareholderEvent[];
  entities: Entity[];
  shareclasses: ShareClass[];
  warnings: EventsImportWarning;
}

export class ShareholderWithStateEvent extends ShareholderEvent {
  state: State;

  constructor({
    state,
    type,
    organizationId,
    creator,
    date,
    price,
  }: {
    state: State;
    type: ShareholderEventType;
    organizationId: string;
    creator: string;
    date: string;
    price?: number;
  }) {
    super({ type, organizationId, creator, date: new Date(date), price });
    this.state = state;
  }
}
export class shareholderMovement {
  entityId: string;
  amount: number;
  numbers: ShareNumbers[];
}

export class EstablishEvent extends ShareholderWithStateEvent {
  constructor({
    state,
    organizationId,
    creator,
    date,
    price,
  }: {
    state: State;
    organizationId: string;
    creator: string;
    date: string;
    price?: number;
  }) {
    super({ state, type: ShareholderEventType.emission, organizationId, creator, date, price });
  }
}
export class StockDilution extends ShareholderEvent {
  share_class: ShareClass = null;
  number_of_new_shares: number = null;
  share_price: number = null;
}

export class CapitalValueEvent extends ShareholderEvent {
  assessedCapitalValue: number;
}
export class EditEvent extends ShareholderWithStateEvent {
  constructor({
    state,
    organizationId,
    creator,
    date,
  }: {
    state: State;
    organizationId: string;
    creator: string;
    date: string;
  }) {
    super({ state, type: ShareholderEventType.edit, organizationId, creator, date });
  }
}

export class ImportEvent extends ShareholderWithStateEvent {
  constructor({
    state,
    organizationId,
    creator,
    date,
  }: {
    state: State;
    organizationId: string;
    creator: string;
    date: string;
  }) {
    super({ state, type: ShareholderEventType.import, organizationId, creator, date });
  }
}

export enum EventDataSubType {
  sale = 'sale',
  inheritance_without_tax_continuity = 'inheritance_without_tax_continuity',
  gift_without_tax_continuity = 'gift_without_tax_continuity',
  taxable_inheritance_gift_with_tax_continuity = 'taxable_inheritance_gift_with_tax_continuity',
  gift_sale = 'gift_sale',
  inheritance_gift_without_tax_continuity = 'inheritance_gift_without_tax_continuity',
  division_divorce = 'division_divorce',
  group_internal_transaction = 'group_internal_transaction',
  transaction_with_tax_continuity = 'transaction_with_tax_continuity',
}
export enum EventDataSubTypeShareDelete {
  Liquidation = 'liquidation',
  Equal_partial_liquidation = 'Equal_partial_liquidation',
  Unequal_partial_liquidation = 'Unequal_partial_liquidation',
  Taxable_merger = 'Taxable_merger',
  Taxable_demerger = 'Taxable_demerger',
  Cancellation_of_treasury_shares_to_cover_losses_and_allocations_to_funds = 'Cancellation_of_treasury_shares_to_cover_losses_and_allocations_to_funds',
  Taxable_redemption_in_connection_with_TaxFree_merger_Or_demerger = 'Taxable_redemption_in_connection_with_TaxFree_merger_Or_demerger',
  Relocation_of_company_to_and_from_Norway = 'Relocation_of_company_to_and_from_Norway',
}
export enum EventDataemissionSubtype {
  share_issue = 'share_issue',
  share_issue_converting_of_claim = 'share_issue_converting_of_claim',
  share_issue_Conerninternal_transfer = 'share_issue_Conerninternal_transfer',
  share_issue_employee_shares = 'share_issue_employee_shares',
}
export enum EventDataDividendSubtype {
  Dividend = 'Dividend',
  Additional_yield = 'Additional_yield',
  Extraordinary_dividends = 'Extraordinary_dividends',
  Refund_of_premium = 'Refund_of_premium',
  Reduction_of_share_capital = 'Reduction_of_share_capital',
  Shareholder_loans = 'Shareholder_loans',
}

export class TransactionEvent extends ShareholderEvent {
  sender: string;
  recipient: string;
  shareclassId: string;
  amount: number;
  subType: EventDataSubType;

  // PJL I had to add to make it see events
  options?: any;

  constructor({
    sender,
    recipient,
    subType,
    date,
    shareclassId,
    amount,
    price,
    organizationId,
    creator,
  }: {
    sender: string;
    recipient: string;
    records: ShareholderRecord[];
    subType: EventDataSubType;
    date: string;
    shareclassId: string;
    amount: number;
    price: number;
    organizationId: string;
    creator: string;
  }) {
    super({
      type: ShareholderEventType.transaction,
      organizationId,
      creator,
      date: new Date(date),
      price,
    });
    this.sender = sender;
    this.recipient = recipient;
    this.subType = subType;
    this.shareclassId = shareclassId;
    this.amount = amount;
  }
}

export interface TransactionInputOptions {
  emailRecipient?: boolean;
  emailSender?: boolean;
}

export class TransactionInputEvent extends ShareholderEvent {
  sender: string;
  recipient: string;
  records: ShareholderRecord[];
  subType: EventDataSubType;
  options: TransactionInputOptions;

  constructor({
    sender,
    recipient,
    subType,
    records,
    date,
    organizationId,
    creator,
    options,
  }: {
    sender: string;
    recipient: string;
    subType: EventDataSubType;
    records: ShareholderRecord[];
    date: Date;
    organizationId: string;
    creator: string;
    options: TransactionInputOptions;
  }) {
    super({
      type: ShareholderEventType.transaction,
      organizationId,
      creator,
      date: new Date(date),
    });
    this.sender = sender;
    this.recipient = recipient;
    this.subType = subType;
    this.records = records;
    this.options = options;
  }
}

// TODO do we make this more light wieght like oehter events.
//

export class EmissionEvent extends ShareholderWithStateEvent {
  // THis stuff not needed ?
  // share_rate: number = null; // Rate/cost of each share
  // investments: InvestmentRecord[] = [];
  // execution_date: Date = new Date();
  // shareclassId: string = null;
  // number_of_new_shares: number = null;
  // share_price: number = null;
  subType?: EventDataemissionSubtype;
  emissionPlanRef?: string;
  emissionPlan?: EmissionPlan;
  dateAddedToTaxRegistery?: string;
  constructor({
    state,
    organizationId,
    creator,
    date,
    dateAddedToTaxRegistery,
    price,
    subType,
    emissionPlanRef,
  }: {
    state: State;
    organizationId: string;
    creator: string;
    date: string;
    dateAddedToTaxRegistery?: string;
    subType?: EventDataemissionSubtype;
    price?: number;
    emissionPlanRef?: string;
  }) {
    super({ state, type: ShareholderEventType.emission, organizationId, creator, date, price });
    this.emissionPlanRef = emissionPlanRef;
    this.subType = subType;
    this.dateAddedToTaxRegistery = dateAddedToTaxRegistery;
  }
}

export interface DividendEvent extends ShareholderWithStateEvent {
  shareclassId: string;
  dividend: number;
  dividendTotal?: number;
  paymentDate?: Date;
  subType?: EventDataDividendSubtype;
}

export enum RoundingStrategy {
  UP = 'UP',
  DOWN = 'DOWN',
}
export enum StatusOptins {
  OPEN = 'OPEN',
  DELETED = 'DELETED',
}
export interface SplitEvent extends ShareholderEvent {
  from: number;
  to: number;
  strategy: RoundingStrategy;
  shareclassId: string;
  parValue?: number;
}
export interface SpliceEvent extends ShareholderEvent {
  from: number;
  to: number;
  strategy: RoundingStrategy;
  shareclassId: string;
  parValue?: number;
}
export enum EventDataParValueSubtype {
  Correction = 'Correction',
  New_issue_increase_par_value = 'New_issue_increase_par_value',
  Increased_par_value_fund_issue = 'Increased_par_value_fund_issue',
}

export interface ShareParvalueChangeEvent extends ShareholderEvent {
  shareclassId: string;
  parValue: number;
  subType?: EventDataParValueSubtype;
  depositPerShare?: number;
}
export interface ShareholderParvalueChangeEventlist {
  eventId: string;
  eventType: string;
  eventDate: Date;
  shareclassId: string;
  parValue: number;
  createdAt?: Date;
}

export class InvestmentRecord {
  _id?: string;
  amount = 0;
  entityId: string = null;
  shareclassId: string = null;
}

export class ShareOption {
  // DEPRECATED
  id: string = null;
  name: string = null;
  underlying: ShareClass = null;
  // strike: number = null;
  amount: number = null;

  assigned_shareholders: ShareholderRecord[] = null;
  // execution_date: Date = null;
  // expiration_date: Date = null;
}

export enum StockContractType {
  OPTION = 'OPTION',
  WARRANT = 'WARRANT',
  CONVERTIBLE = 'CONVERTIBLE',
}

export enum ContractStatus {
  NOT_EXERCISED = 'NOT_EXERCISED',
  CANCELLED = 'CANCELLED',
  EXPIRED = 'EXPIRED',
  EXERCISED = 'EXERCISED',
}

export interface StockContract {
  _id?: MongoId;
  title?: string;
  shareclass: ShareClass;
  entity: Entity;
  amount: number;
  sizeRemaining: number;
  expirationDate?: Date;
  strike: number;
  awaitingConditions: boolean;
  conditions?: string;
  type: StockContractType;
  direntId?: string;
  attachments?: any[];
  status?: ContractStatus; // ENUM
  organizationId?: string;
  useHistory?: any[];
}

export interface StockOption extends StockContract {}

export interface StockWarrant extends StockContract {}
export interface ConvertibleLoan extends StockContract {
  _id?: MongoId;
  size?: number;
  convertiblePart?: number;
  interestPerYear?: number;
  latePaymentIntereset?: number;
  // nominal interest might be important
  paymentStructure?: string;
}
export interface AuthUse {
  amount: number;
  date: Date;
}
export interface ContractUse {
  amount: number;
  date: Date;
}

export enum StockAuthorizationType {
  ISSUESHARES = 'ISSUESHARES',
  CONVERTIBLE = 'CONVERTIBLE',
  BUYSHARES = 'BUYSHARES',
  CLAIM = 'CLAIM',
  OPTIONS = 'OPTIONS',
  DIVIDENDS = 'DIVIDENDS',
}
export enum AuthorizationStatus {
  NOT_EXERCISED = 'NOT_EXERCISED',
  CANCELLED = 'CANCELLED',
  EXPIRED = 'EXPIRED',
  EXERCISED = 'EXERCISED',
}
export interface StockAuthorization {
  _id?: string;
  type: StockAuthorizationType;

  startDate: Date;
  expirationDate: Date;
  registrationDate: Date;
  size: number;
  sizeRemaining: number;
  useHistory: AuthUse[];
  direntId?: string;

  notes?: string;
  attachments?: any[];

  status?: AuthorizationStatus; // ENUM
}
export interface StockIssueShares extends StockAuthorization {}
export interface StockConvertible extends StockAuthorization {
  loansize: number;
}
export interface StockBuyShares extends StockAuthorization {}
export interface StockClaim extends StockAuthorization {}
export interface StockOptionsAuth extends StockAuthorization {}
export interface StockDividendsAuth extends StockAuthorization {}

export interface CapTableRecord {
  name: string;
  entity: Entity;
  shares: ShareholderRecord[];
  options: StockOption[];
  warrants: StockWarrant[];
  loans: ConvertibleLoan[];
  currentWarrants: number;
  currentLoans: number;
  currentOptions: number;
  currentShares: number;
  currentContracts: number;
  currentDiluted: number;
  contractPrice: number;
  valueDistribution: number;
}

export class ShareholderRecord {
  entityId: string | null = null;
  amount = 0;
  shareclassId: string | null = null;
  notes? = '';
  importData?: ImportShareholder;
  dirty?;
  numbers?: ShareNumbers[];
  price?: number;
  accounttype?: string;
  _id?: string;

  static clone(a: ShareholderRecord): ShareholderRecord {
    const clone = new ShareholderRecord();
    clone.shareclassId = a.shareclassId;
    clone.amount = a.amount;
    clone.entityId = a.entityId;
    return clone;
  }
}

export class ImportShareholder {
  shareclass?: string;
  shareholder?: string;
  dob_orgno?: string;
  address?: string;
  noOfShares?: string;
  isCompany?: boolean;
}
export class TaxIncentives {
  _id: string = null;
  year: string;
  organizationId: string;
  investors: InvestorTaxIncentive[];
}
export class InvestorTaxIncentive {
  _id: string;
  eventId: string;
  date: Date;
  amount: number;
  entityId?: string;
}
export class InvesorValues {
  entityId: string;
  eventId: string;
  total: number;
  amount: number;
}
// See also lib-mongoose schemas
export class Entity extends ImportShareholder {
  _id: MongoId = null;
  organizationId = null; // Company owned by entity.
  orgnr?: string = null; // Owners org number (Only if an organisation)
  public override isCompany?: boolean = false;
  isSubsidiary?: boolean = false;
  email?: string = null;
  dirty? = true;
  dob?: string;
  pnr?: string;
  citizenship?: string;
  tlf?: string = null;
  utlCode?: string = null;
  membershipnumber?: string;
  contactName?: string;
  lei?: string;
  vps?: string;
  accounttype?: string;
  // THhis appears to duplicate dob
  ssn?: string = null;

  //   importData: ImportShareholder = null; // only used when importing data

  shareholderRole? = null; // TODO ???
  right_of_first_refusal?: boolean = null; // TODO ???
  firstName?: string = null;
  lastName?: string = null;
  companyname?: string = null;

  // TAX -office data DEPRECATED ?
  tax_shareclass?: string;
  tax_shareholder?: string;
  tax_dob_orgno?: string;
  tax_address?: string;
  tax_noOfShares?: string;
  tax_isCompany?: boolean;

  // Bisnode
  internref?: string = null; // Bisnode reference

  // other stuff
  mobile?: string = null;
  jobtlf?: string = null;
  //   address_details: string = null;
  public override address?: string = null;
  zip?: string = null;
  city?: string = null;
  country?: string;
  //   country: string = null;
  banknr?: string = null;
  residence_address?: string = null;
  residence_zip?: string = null;
  residence_city?: string = null;
  users?: BaseUser[];

  // Local fields
  attending?: boolean;
  attendingWithProxy?: boolean;
  attendingPhysical?: boolean;
  attendingWithPreVoting?: boolean;
  attendingWithVisiting?: boolean;
  inviteeData?: MeetingShareholderInvitee;
  entityShares?: number;
  deleted?: boolean;
  displayName?: string;
  displayIdentity?: string;
  displayEmail?: string;
  entityVotes?: number;
  entityCapital?: number;
  shareClassMap?: {
    [classId: string]: {
      voteWeight: number;
      className: string;
      sharesInClass: number;
      votesInClass: number;
      capitalInClass: number;
      shareClassesCount: number;
    };
  };

  // this lot of 4 items needed for filtering by email in entity table
  // and for displaying names
  entityRepresentative?: string;
  entityRepresentativeEmail?: string;
  entityRepresentativeProxy?: string;
  entityRepresentativeProxyEmail?: string;

  marked?: boolean;

  // passed from fronted to create a new  Entity.
  createMe?: boolean;
  note?: string;

  static getFirstName(entity: Entity) {
    if (entity.firstName) return entity.firstName;
    if (entity.users && entity.users.length > 0) {
      return entity.users[0].firstName;
    }
    return '';
  }

  static getLastName(entity: Entity) {
    if (entity.lastName) return entity.lastName;
    if (entity.users && entity.users.length > 0) {
      return entity.users[0].lastName;
    }
    return '';
  }

  static getFullName(entity: Entity) {
    return (
      ((Entity.getFirstName(entity) || '') + ' ' + (Entity.getLastName(entity) || '')).trim() ||
      fallbackBoardmemberAlikeName(entity) ||
      '-'
    );
  }

  static getDisplayName(p: Entity) {
    if (!p) return '';
    let ret = '';

    if (p.isCompany) {
      ret = p.companyname || '-';
    } else {
      ret = Entity.getFullName(p);
    }

    if (p.deleted) {
      ret += ' (Zombie)';
    }
    return ret;
  }

  static getDisplayEmail(entity: Entity) {
    let result = '';

    if (entity && entity.users && entity.users.length > 0) {
      let temp = [];
      for (const contact of entity.users) {
        if (contact.email) {
          temp.push(contact.email);
        }
      }
      result = temp.join(', ');
    }

    return result;
  }
  static getSearchNameAndEmail(p: Entity) {
    if (!p) return '';
    let ret;

    if (p.isCompany) {
      ret = p.companyname || '-';
    } else {
      ret = Entity.getFullName(p);
    }

    for (const user of p.users) {
      ret = ret + ' ' + user.firstName + ' ' + user.lastName + ' ' + user.email;
    }

    if (p.deleted) {
      ret += ' (Zombie)';
    }
    return ret;
  }
}

export type MinimalistEntity = {
  _id: string;
  name: string;
  fnr?: string;
  pnr?: string;
  orgnr?: string;
  isCompany?: boolean;
  _ids?: string[];
};

export class DeletionData {
  entity: Entity;
  amount: number;
  shareclass: ShareClass;
  date?: Date;
  subType?: string;
}
export class moveSharesToClassData {
  entity: Entity;
  amount: number;
  fromshareclass: string;
  toshareclass: string;
  date?: Date;
}
export interface moveSharesToClassEvent extends ShareholderEvent {
  sender: string;
  amount: number;
  shareclassId: string;
  toShareclassId: string;
}
export interface ShareDeletionDataEvent extends ShareholderEvent {
  sender: string;
  amount: number;
  shareclassId: string;
  subType?: string;
}

export class UserEntity {
  userId: string;
  entityId: string;
}

export class ShareholderOnboardPayload {
  entities: Entity[];
  shareclasses: ShareClass[];
  additionalContacts: any; // map of entityId to  {email name etc}
  option: ShareholderEventType;
  state?: State;
  sender?: string;
  recipient?: string;
  records?: ShareholderRecord[];
  subType?: EventDataSubType;
  date?: string;
  emailSender?: boolean;
  emailRecipient?: boolean;
  price?: number;
  from?: number;
  to?: number;
  strategy?: string;
  shareclassId?: string;
  vps?: string;
  lei?: string;
  parvalue?: number;
  //contract information related to stock contract
  contracts?: StockContract[];
  contractedAmount?: number;
  contractExpiryDate?: string;
  contractTitle?: string;
  contractType?: string;
  strikePrice?: string;
  totalLoan?: string;
  effectiveInterestRate?: string;
  convertibleShareOfLoan?: string;
  contractTerms?: string;
  contractPaymentStructure?: string;
  contractStatus?: string;
}
export class entityImportOnboardPayload {
  entities: Entity[];
  additionalContacts: any; // map of entityId to  {email name etc}
}

export enum BeneficialOwnerCriteria {
  direct_ownership = 'direct_ownership',
  indirect_ownership = 'indirect_ownership',
  owner_by_contract = 'owner_by_contract',
  // other = 'other',
}

export enum BeneficialOwnerInterval {
  less_than_25 = 'lessthan25',
  is_25_to_49 = 'is_25_to_49',
  is_50_to_74 = 'is_50_to_74',
  is_75_to_100 = 'is_75_to_100',
}

export const BeneficialOwnerIntervalMap = {
  lessthan25: '< 25 %',
  is_25_to_49: '25 % - 49.99 %',
  is_50_to_74: '50 % - 74.99%',
  is_75_to_100: '75 % - 100%',
} as const;

export enum BeneficialOwnerPositions {
  has_percentage_ownership = 'has_percentage_ownership',
  has_percentage_voting_rights = 'has_percentage_voting_rights',
  has_50_percent_power = 'has_50_percent_power',
  has_other_position = 'has_other_position',
}

export class Suggestion {
  firstName: string;
  lastName: string;
  hasDirectOwnership?: boolean;
  hasIndirectOwnership?: boolean;
  note?: string;
  percentageOwned?: number;
  percentageVotes?: number;
  indirectVotingShare?: number;
  relationships?: Relationship[];
  source?: string;
  address?: string;
  dob?: string;
  fnr?: string;
  pnr?: string;
  role?: string;
  email?: string;
  phone?: string;
}
export class Relationship {
  person?: string;
  orgNr?: string;
  target?: string;
  targetOrgNr?: string;
  depth?: number;
  name?: string;
  directVotingShare?: number;
  indirectVotingShare?: number;
  controlPropagated?: boolean;
  companyName?: string;
}
export class BeneficialOwnersVersion {
  organizationId: string;
  state: BeneficialOwnersState;
  owners?: BeneficialOwner[];
  date?: Date;
  note?: string;
  fileAttachments?: any[];
  parentIdForFiles?: string;

  isPublicOrganization?: boolean;
  isForeignPublicCompany?: boolean;

  dateNotificationSent?: Date;
  userNotificationSent?: string;
  dateAltinnSent?: Date;
  userAltinnSent?: string;

  isCopy?: boolean;
  done?: boolean;
  deleted?: boolean;
  _id?: string;
  createdAt?: any;
  updatedAt?: any;
  //if the company has one or more unidentified owners
  unidentifiedOwners?: boolean;
  //For foundations, this specifies something
  roleHolderInAddition?: boolean;
  //For foreign companies (NUF)
  foreignRegister?: ForeignRegister;
  //For ASA is registered on norweigan stock market
  stockMarket?: PublicMarkets;

  //legacy
  noneReasons?: BeneficialOwnersNoneReasons[];
}
export class IntermediateCompany {
  isForeign: boolean;
  orgNumber: string;
  name?: string;
  address?: string;
  foreignCountry?: ISONationalCode2Letter;
}

export class BeneficialOwner {
  _id?: string;
  version?: string;

  //Required personal info
  firstName: string;
  lastName: string;
  isIncludedInTheNationalPopulationRegisterNorway: boolean;
  fnr?: string;
  dob?: Date;
  foreignCitizenship?: ISONationalCode3Letter[];
  foreignCountryOfResidence?: ISONationalCode3Letter;

  positions?: BeneficialOwnerPositions[];
  percentageOwned?: BeneficialOwnerInterval;
  criteria?: BeneficialOwnerCriteria[];
  intermediateCompanies?: IntermediateCompany[];
  otherPositionReason?: string;

  percentageVotes?: BeneficialOwnerInterval;
  criteriaVoting?: BeneficialOwnerCriteria[];
  intermediateCompaniesVoting?: IntermediateCompany[];

  //Extra information
  email?: string;
  phone?: string;
  note?: string;
  role?: string;

  //legacy stuff that is not used anymore
  countryOfResidence?: string;
  citizenship?: string;
}

export enum PublicMarkets {
  none = 'none',
  euronextOslo = 'markedstype.euronextOslo',
  euronextExpandOslo = 'markedstype.euronextExpandOslo',
}
export enum ForeignRegister {
  none = 'none',
  belgium = 'registertype.registerOverReelleRettighetshavereIBelgia',
  bulgaria = 'registertype.registerOverReelleRettighetshavereIBulgaria',
  denmark = 'registertype.registerOverReelleRettighetshavereIDanmark',
  estonia = 'registertype.registerOverReelleRettighetshavereIEstland',
  finland = 'registertype.registerOverReelleRettighetshavereIFinland',
  france = 'registertype.registerOverReelleRettighetshavereIFrankrike',
  greece = 'registertype.registerOverReelleRettighetshavereIHellas',
  ireland = 'registertype.registerOverReelleRettighetshavereIIrland',
  iceland = 'registertype.registerOverReelleRettighetshavereIIsland',
  italy = 'registertype.registerOverReelleRettighetshavereIItalia',
  croatia = 'registertype.registerOverReelleRettighetshavereIKroatia',
  cyprus = 'registertype.registerOverReelleRettighetshavereIKypros',
  latvia = 'registertype.registerOverReelleRettighetshavereILatvia',
  liechtenstein = 'registertype.registerOverReelleRettighetshavereILiechtenstein',
  lithuania = 'registertype.registerOverReelleRettighetshavereILitauen',
  luxembourg = 'registertype.registerOverReelleRettighetshavereILuxembourg',
  malta = 'registertype.registerOverReelleRettighetshavereIMalta',
  netherlands = 'registertype.registerOverReelleRettighetshavereINederland',
  poland = 'registertype.registerOverReelleRettighetshavereIPolen',
  portugal = 'registertype.registerOverReelleRettighetshavereIPortugal',
  romania = 'registertype.registerOverReelleRettighetshavereIRomania',
  slovakia = 'registertype.registerOverReelleRettighetshavereISlovakia',
  slovenia = 'registertype.registerOverReelleRettighetshavereISlovenia',
  spain = 'registertype.registerOverReelleRettighetshavereISpania',
  sweden = 'registertype.registerOverReelleRettighetshavereISverige',
  czechRepublic = 'registertype.registerOverReelleRettighetshavereITsjekkia',
  germany = 'registertype.registerOverReelleRettighetshavereITyskland',
  hungary = 'registertype.registerOverReelleRettighetshavereIUngarn',
  austria = 'registertype.registerOverReelleRettighetshavereIOesterrike',
}

export enum BeneficialOwnersState {
  identified = 'identified',
  none_identified = 'none_identified',
  none = 'none',
  null = 'null',
}

export enum BeneficialOwnersNoneReasons {
  government_entity = 'government_entity',
  government_company = 'government_company',
  municipality_company = 'municipality_company',
  church_entity = 'church_entity',
  deposit_guarantee_scheme = 'deposit_guarantee_scheme',
  student_welfare_organization = 'student_welfare_organization',
  health_trust = 'health_trust',
  foreign = 'foreign',
  other_reason = 'other_reason',
}

// If address field is null try to fix from other data.
export function fixEntityAddress(e: Entity): any {
  if (!e) return;
  if (e.address) {
    return e.address;
  } else if (e.tax_address) {
    e.address = e.tax_address;
  } else if (e.residence_address) {
    e.address = e.residence_address;
  } else if (!!e.city && !!e.zip) {
    e.address = e.zip + ' ' + e.city;
  } else if (!!e.residence_city && !!e.residence_zip) {
    e.address = e.residence_city + ' ' + e.residence_zip;
  }
  return e.address;
}
export const listofFLIlike = [
  'FLI',
  'NIF',
  'SA',
  'STI',
  'FKF',
  'KF',
  'IKS',
  'IKSR',
  'SBSTI',
  'ANS',
  'DA',
  'SAM',
];

// This is needed for shared user list + entities list while invitation
function fallbackBoardmemberAlikeName(entity) {
  let result = '';
  if (entity.first_name) {
    result = entity.first_name;
  }

  if (entity.last_name) {
    if (result) {
      result += ' ';
    }

    result += entity.last_name;
  }

  return result;
}
